import React from 'react';
import PropTypes from 'prop-types';
import combineStyles from 'classnames';
import { makeStyles } from '~/tools/makeStyles';
import * as QUESTION_TYPES from '../constants/question_types';
import {
  IconPreview as QuestionIcon,
  DropDownPreview as QuestionDropDown,
  ENPSPreview as QuestionENPS,
  MultipleChoicePreview as QuestionMultipleChoice,
  NewMessagePreview as QuestionMessage,
  RatingPreview as QuestionRating,
  TextPreview as QuestionText,
  YesNoPreview as QuestionYesNo,
} from '@worktango/shared-frontend';
import { useTranslation } from 'react-i18next';

const MAX_WIDTH = 800;

const useStyles = makeStyles((theme) => ({
  container: {
    position: 'relative',
    height: '100%',
    width: '100%',
    overflowX: 'hidden',
    overflowY: 'auto',
    '-ms-overflow-style': 'auto',
    wordBreak: 'break-word',
    '& img': {
      maxWidth: '100%',
    },
  },
  hide: {
    // NOTE: don't use `display: none` (it breaks responsive size calculations in question components)
    visibility: 'hidden',
  },
  content: {
    position: 'absolute',
    top: '10%',
    left: '50%',
    '-ms-transform': 'translate(-50%, 0)',
    transform: 'translate(-50%, 0)',
    width: '100%',
    maxWidth: MAX_WIDTH,
    margin: `0 auto ${theme.measurements.footerHeight}px auto`,
    padding: 40,
    [theme.breakpoints.down('sm')]: {
      padding: 25,
      width: 'calc(100% - 50px)',
      margin: `0 auto ${theme.measurements.smallScreenFooterHeight}px auto`,
    },
  },
  footerSpace: {
    height: theme.measurements.footerHeight,
    [theme.breakpoints.down('sm')]: {
      height: theme.measurements.smallScreenFooterHeight,
    },
  },
}));

const monoLingualOptions = (options, lang) =>
  options.map((opt) => ({
    id: opt.id,
    position: opt.position,
    text: opt.languages[lang],
  }));

const byOptionPosition = (a, b) => a.position - b.position;

// Map questions to components based on their type.
const QuestionView = ({ lang, question, ...commonProps }) => {
  const { t } = useTranslation();

  if (!question) return null;

  switch (question.type) {
    case QUESTION_TYPES.ENPS:
      return (
        <QuestionENPS
          minRating={question.ratingLow}
          maxLabel={question.languages[lang].labelHigh}
          minLabel={question.languages[lang].labelLow}
          {...commonProps}
        />
      );
    case QUESTION_TYPES.DROPDOWN:
      return (
        <QuestionDropDown
          options={monoLingualOptions(question.options, lang).sort(byOptionPosition)}
          ariaLabel={t('dropdown.ariaLabel', { question: commonProps.title })}
          {...commonProps}
        />
      );
    case QUESTION_TYPES.ICONS:
      return (
        <QuestionIcon
          iconType={question.icon}
          maxLabel={question.languages[lang].labelHigh}
          minLabel={question.languages[lang].labelLow}
          {...commonProps}
        />
      );
    case QUESTION_TYPES.MULTIPLE_CHOICE:
      return (
        <QuestionMultipleChoice
          options={monoLingualOptions(question.options, lang).sort(byOptionPosition)}
          maxSelectedOption={question.maxRequiredSelections}
          minSelectedOption={question.minRequiredSelections}
          {...commonProps}
        />
      );
    case QUESTION_TYPES.MESSAGE:
      // NOTE: For this question type, `title` and `description` are backwards.
      return (
        <QuestionMessage
          {...commonProps}
          title={question.languages[lang].description}
          description={question.languages[lang].title}
        />
      );
    case QUESTION_TYPES.RATING:
      return (
        <QuestionRating
          iconType={question.icon}
          maxRating={question.ratingHigh}
          maxLabel={question.languages[lang].labelHigh}
          minLabel={question.languages[lang].labelLow}
          {...commonProps}
        />
      );
    case QUESTION_TYPES.TEXT:
      return <QuestionText placeholder={t('feedback.placeholder')} {...commonProps} />;
    case QUESTION_TYPES.YES_NO:
      return <QuestionYesNo iconType={question.icon} {...commonProps} />;
    default:
      return null;
  }
};

// High level question component; maps common attributes.
const Question = ({
  titleRefs,
  lang,
  question = {
    isRequired: false,
    hasSkipButton: true,
    hasDescription: false,
    isCommentable: false,
  },
  screenReaderLabel,
  answer,
  onChooseAnswer,
  onCommentChange,
  isActive = false,
}) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const questionLanguage = question.languages[lang];

  const translation = {
    means: t('accessbility.means'),
    clickSelect: t('accessbility.clickSelect'),
    continueButton: t('accessbility.continueButton'),
    skipButton: t('button.skip'),
    okButton: t('swal.button.ok'),
    arialContinue: t('accessbility.continueSurvey'),
    arialSkip: t('accessbility.skipQuestion'),
    arialOk: t('accessbility.goNext'),
    required: t('required'),
  };

  // Need to make sure the tabIndex for srOnly element is 0, otherwise it won't be able work
  return (
    <div
      className={combineStyles(styles.container, {
        [styles.hide]: !isActive, // need to do this to prevent user go to next question by tab
      })}
    >
      <div className={styles.content}>
        <QuestionView
          lang={lang}
          titleRef={(el) => {
            if (titleRefs && titleRefs.current) titleRefs.current[question.id] = el;
          }}
          question={question}
          questionId={question.id}
          title={questionLanguage.title}
          titleAriaLabel={screenReaderLabel}
          commentAriaLabel={t('text.ariaLabel', { question: questionLanguage.title })}
          description={questionLanguage.description}
          isRequired={question.isRequired}
          showSkipButton={question.hasSkipButton}
          showDescription={question.hasDescription}
          showTextComment={question.isCommentable}
          savedAnswer={answer}
          handleAnswer={onChooseAnswer}
          onCommentChange={onCommentChange}
          commentsLabel={t('comment.title')}
          commentsPlaceholder={t('comment.placeholder')}
          allowKeyboardSelection={isActive}
          translation={translation}
        />
        <div className={styles.footerSpace} />
      </div>
    </div>
  );
};

Question.propTypes = {
  lang: PropTypes.string,
  question: PropTypes.shape({
    id: PropTypes.number.isRequired,
    position: PropTypes.number.isRequired,
    type: PropTypes.string.isRequired,
    icon: PropTypes.string,
    isRequired: PropTypes.bool,
    isCommentable: PropTypes.bool,
    hasDescription: PropTypes.bool,
    hasLabels: PropTypes.bool,
    hasSkipButton: PropTypes.bool,
    languages: PropTypes.objectOf(
      PropTypes.shape({
        title: PropTypes.string.isRequired,
        description: PropTypes.string,
        labelLow: PropTypes.string,
        labelHigh: PropTypes.string,
      }),
    ).isRequired,
    minRequiredSelections: PropTypes.number,
    maxRequiredSelections: PropTypes.number,
    ratingLow: PropTypes.number,
    ratingHigh: PropTypes.number,
    options: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        position: PropTypes.number,
        languages: PropTypes.objectOf(PropTypes.string),
      }),
    ),
  }),
  answer: PropTypes.object,
  onChooseAnswer: PropTypes.func,
  screenReaderLabel: PropTypes.string,
  announceRef: PropTypes.object,
  isActive: PropTypes.bool,
};

export default Question;
